import * as DOMUtils from "history/DOMUtils";
import { PdpSection } from "./constants";
import type { UscStateType } from "../redux/store";
import { type DfServiceHistoryCode, DfVehicleStatus } from "../../shared-logic/utils/dfConstants";
import { type ServiceHistoryEventType, type VehicleType, UscContext } from "../../shared-logic/types/UscCommonTypes";
import {
    type DealerInfoItemType,
    getEnabledServices,
    getVehicleFullName,
    hasCustomerPromiseBannerItems,
} from "./helpers";
import type { ButtonVariantsType } from "../../shared-components/toyota/styles/atoms/Button";
import type { DayCodeType, DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import { type UscLabelType, getUscLabel, getUscOptionalLabel } from "./labels";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import { AEM_AUTHOR_URL_SUFFIX, isRetailer } from "../../../common-deprecated/utils";
import type { TyreInfoLabels } from "../../../shared-logic/utils/tyreInfoContants";
import { vehicleHasWarranty } from "../../shared-logic/utils/uscUtils";
import { enableUscInsurance } from "./settings";
import type { UscVehicleReducerType } from "../redux/reducers/UscVehicleReducer";
import { COMPONENT_USED_STOCK_CARS_PDF } from "../../../shared-logic/server/components";
import { getWindowLocation } from "../../../shared-logic/utils/commonUtils";

// ----------------------------------------------------------------------
// PDP sections visibility
// ----------------------------------------------------------------------

// Sections hidden when the vehicle is sold.
const disabledWhenSold: PdpSection[] = [
    PdpSection.Services,
    PdpSection.Accessories,
    PdpSection.CarDetails,
    PdpSection.Insurance,
    PdpSection.CustomerPromise,
    PdpSection.AdditionalCtas,
];

// Sections shown only when the vehicle is available.
const enabledWhenAvailable: PdpSection[] = [];

export const pdpSectionSelector = (state: UscStateType): PdpSection[] => {
    const { uscSettings, vehicle, accessories, dealer, commonSettings } = state;

    const enableServiceHistory =
        uscSettings.enableServiceHistory &&
        !!historyEventsSelector(vehicle.result, uscSettings.visibleVehicleHistoryEvents).length;
    const enableEquipment = !!vehicle.result?.product.equipment.length;
    const enableSpecCategories = !!uscSettings.specCategories.length;
    const enableServices = !!getEnabledServices(vehicle, uscSettings, dealer.dealer).length;
    const enableDealer = dealer.initialized && !dealer.failed;
    const enableAccessories =
        uscSettings.enableAccessories && (accessories.accessoryResults.length || accessories.packResults.length);
    const enableCarDetailsSection = enableEquipment || enableServiceHistory || enableSpecCategories;
    const enableCustomerPromiseBanner =
        hasCustomerPromiseBannerItems(
            uscSettings,
            vehicle.result,
            commonSettings.brand,
            vehicle.type,
            commonSettings,
        ) &&
        vehicle.type === UscContext.Used &&
        vehicleHasWarranty(vehicle.result);
    const enableInsurance =
        vehicle.result && enableUscInsurance(commonSettings, uscSettings, vehicle.result, vehicle.type);
    const onRetailerScreen = isRetailer(commonSettings);

    return uscSettings.pdpSections.filter((pdpSection) => {
        if (pdpSection === PdpSection.Services && !enableServices) return false;
        if (pdpSection === PdpSection.Accessories && !enableAccessories) return false;
        if (pdpSection === PdpSection.CarDetails && !enableCarDetailsSection) return false;
        if (pdpSection === PdpSection.DealerInfo && !enableDealer) return false;
        if (pdpSection === PdpSection.CustomerPromise && !enableCustomerPromiseBanner) return false;
        if (pdpSection === PdpSection.Insurance && !enableInsurance) return false;
        if (pdpSection === PdpSection.AdditionalCtas && onRetailerScreen) return false; // Additional CTAs are not shown on retailer screens.

        // Note that the disabledWhenSold and enabledWhenAvailable use the inverse approach, this is intentional as it was the easiest to maintain.
        if (vehicle.result?.vehicleStatus.code === DfVehicleStatus.Sold && disabledWhenSold.includes(pdpSection)) {
            return false;
        } else if (
            vehicle.result?.vehicleStatus.code !== DfVehicleStatus.Available &&
            enabledWhenAvailable.includes(pdpSection)
        ) {
            return false;
        }
        return true;
    });
};

export const historyEventsSelector = (
    vehicle: VehicleType | null,
    visibleVehicleHistoryEvents: DfServiceHistoryCode[],
): ServiceHistoryEventType[] => {
    return vehicle?.history?.events?.filter((event) => visibleVehicleHistoryEvents.includes(event.type.code)) || [];
};

/**
 * This only applies to Toyota buttons on the PDP.
 * UCL uses red (primary) buttons,
 * SCL uses black (primary-dark) buttons.
 */
export const getToyotaButtonVariant = (context: UscContext, blackPrimaryCtaExp?: boolean): ButtonVariantsType => {
    // UC-1376: Black CTA A/B testing experiment
    if (blackPrimaryCtaExp) return "primary-dark";

    return context === UscContext.Used ? "primary" : "primary-dark";
};

/**
 * returns the correct pageDisclaimerLabel based on vehicleType and financeOption
 */
export const getPageDisclaimerLabel = (financeOption: FinanceOptionType, vehicleType: UscContext): UscLabelType => {
    if (vehicleType === UscContext.Used) {
        if (financeOption === "cash") return "uscPageDisclaimerUsedCash";
        if (financeOption === "monthly") return "uscPageDisclaimerUsedMonthly";
    }
    if (vehicleType === UscContext.Stock) {
        if (financeOption === "cash") return "uscPageDisclaimerStockCash";
        if (financeOption === "monthly") return "uscPageDisclaimerStockMonthly";
    }
    throw new Error("Cannot find correct disclaimer label key for current vehicle type and finance option");
};

const weekdayDateMap: Record<DayCodeType, string> = {
    MON: "2021-01-04T00:00:00.000Z",
    TUE: "2021-01-05T00:00:00.000Z",
    WED: "2021-01-06T00:00:00.000Z",
    THU: "2021-01-07T00:00:00.000Z",
    FRI: "2021-01-08T00:00:00.000Z",
    SAT: "2021-01-09T00:00:00.000Z",
    SUN: "2021-01-10T00:00:00.000Z",
};

/**
 * convert day codes from A2D codes to translations directly form culture object
 */
export const localizeDayCodes = (code: DayCodeType, culture: CommonSettingsType["culture"]): string => {
    const day = new Intl.DateTimeFormat(culture.name, { weekday: "long" }).format(new Date(weekdayDateMap[code]));
    return `${day.charAt(0).toUpperCase()}${day.slice(1)}`;
};

/**
 * Converts the time received from the Toyota API to a HH:MM 24hour notation.
 * ⚠ This helper function is a copy of the one GFI is using.
 * @param time (example: 10 PM, 11:30 AM)
 * @returns {string} (example: 22:00, 11:30)
 */
export const convertDealerOpeningTime = (time: string): string => {
    if (time) {
        let hours = Number(time.match(/^(\d+)/)![1]);
        let minutes = 0;
        if (time.indexOf(":") > -1) {
            minutes = Number(time.match(/:(\d+)/)![1]);
        }
        const AMPM = time.match(/\s(.*)$/)![1];
        if (AMPM === "PM" && hours < 12) {
            hours += 12;
        }
        if (AMPM === "AM" && hours === 12) {
            hours -= 12;
        }
        let sHours = hours.toString();
        let sMinutes = minutes.toString();
        if (hours < 10) {
            sHours = `0${sHours}`;
        }
        if (minutes < 10) {
            sMinutes = `0${sMinutes}`;
        }
        return `${sHours}:${sMinutes}`;
    } else {
        return "00:00";
    }
};

/**
 * Parse the opening hours of an A2D sourced dealer into something that can be properly used in the frontend.
 */
export const parseDealerOpeningHours = (
    openingHours: DealerResultType["openingHours"],
    culture: CommonSettingsType["culture"],
): { days: string; hours: string[] }[] => {
    return openingHours.map((openingHour) => ({
        days:
            localizeDayCodes(openingHour.start, culture) +
            (openingHour.end ? `-${localizeDayCodes(openingHour.end, culture)}` : ""),
        hours: [...openingHour.openingHours] // New array to make sure sort works properly.
            .sort((a, b) => {
                // Data returned from A2D is not sorted properly, so use this hacky way to sort the opening hours based on the first hour shown.
                const fromA = convertDealerOpeningTime(a.from).split(":");
                const fromB = convertDealerOpeningTime(b.from).split(":");

                return (
                    new Date(0, 0, 0, Number(fromA[0]), Number(fromA[1])).getTime() -
                    new Date(0, 0, 0, Number(fromB[0]), Number(fromB[1])).getTime()
                );
            })
            .map((hours) => `${convertDealerOpeningTime(hours.from)} - ${convertDealerOpeningTime(hours.to)}`),
    }));
};

export const getFormattedServiceEventDate = (value: string, cultureName: string): string => {
    return new Intl.DateTimeFormat(cultureName, { month: "long", year: "numeric" }).format(new Date(value));
};

/**
 * Creates a unique vehicle name and dealer info (if it exists), used in Carousel images.
 */
export const getHeaderImageAltTag = (vehicleResult: VehicleType | null, dealer: DealerResultType | null): string => {
    if (!vehicleResult || !vehicleResult.product) return "";

    if (!dealer) return getVehicleFullName(vehicleResult);

    const { product } = vehicleResult;
    const { modelYear, brand, model, versionName } = product;
    const { city, region } = dealer.address;
    return `${modelYear} ${brand.description} ${model} - ${city} ${region} - ${versionName} - ${dealer.name}`;
};

export const tyreInfoUscLabelSelector = (state: UscStateType): TyreInfoLabels => ({
    tyreInfo: getUscLabel(state, "uscPdpTyreInfo"),
    tyreInfoNoEprelFallback: getUscLabel(state, "uscPdpTyreInfoNoEprelFallback"),
    tyreInfoDescription: getUscOptionalLabel(state, "uscPdpTyreInfoDescription"),
    tyreInfoFallbackDescription: getUscLabel(state, "uscPdpTyreInfoFallbackDescription"),
    tyreInfoFallback: getUscLabel(state, "uscPdpTyreInfoFallback"),
});

export const enable360Spin = (vehicle: VehicleType | null): boolean => {
    return (
        !!vehicle?.images.length && // the entire carousel is hidden in this case
        vehicle.threeSixtyView?.viewFormat.code === "1" && // the localized version of this code is StampytSHA512
        !!vehicle.threeSixtyView?.parameters[0].value
    );
};

/**
 * Utility function used to know if we're on a USC Retailer Landing-specific PDP
 * This is done by comparing the current PDP URL to the PDP url in pageUrls.
 * If it is different (which means we overrided it coming from the Car Filter, see carFilterPostFetch)
 * we know we're on a USC Retailer Landing PDP.
 */
export const isUscRetailerLandingPdp = (uscSettings: UscSettingsType): boolean => {
    if (!DOMUtils.canUseDOM) return false;

    const { detailPageUrl } = uscSettings; // pageUrls[context].pdp.link

    const actualUrlDetailPage = getWindowLocation().href || "";

    // Inverse because we want to return true if the URL is different (indicating we're on a retailer landing PDP)
    return !actualUrlDetailPage.includes(detailPageUrl.replace(AEM_AUTHOR_URL_SUFFIX, ""));
};

/**
 * When enabled the legal eco label will be shown instead of the consumption section
 * This is currently hardcoded for TDG stock cars
 * This will be implemented in UC-561
 */
export const enableLegalEcoLabel = (
    country: string,
    uscContext: UscContext,
    settings: UscSettingsType,
    overwriteSetting?: boolean,
): boolean => {
    return country === "de" && uscContext === UscContext.Stock && (overwriteSetting || settings?.enableTDGEcolabel);
};

/**
 * Open Detail pdf with tyCode, will fallback to using vehicleForSaleId if not set
 */
export const getUscPdfUrl = (
    commonSettings: CommonSettingsType,
    vehicle: UscVehicleReducerType,
    tyCode?: string | null,
): string => {
    const vehicleQueryParam = tyCode ? `tyCode=${tyCode}` : `vehicleForSaleId=${vehicle.result?.id || ""}`;
    const uscEnvParam = commonSettings.query.uscEnv ? `&uscEnv=${commonSettings.query.uscEnv}` : "";

    return (
        `${commonSettings.resourcePath}/${commonSettings.country}/${commonSettings.language}/` +
        `${COMPONENT_USED_STOCK_CARS_PDF}?uscContext=${vehicle.type}` +
        `&${vehicleQueryParam}&brand=${commonSettings.brand}${uscEnvParam}`
    );
};

/**
 * Hacky code alert!
 * A dealer link can be one of two things:
 * 1. A dealer website (e.g. toyotauppsala.se/)
 * 2. A retailer page (e.g. https://www.toyota.fr/dealer/00cd2-cff39-3c11c-bd600-01200-1)
 * We need to be able to differentiate between these two types of link, and the only way to do that is to check the URL.
 * Specifically, we check if the URL contains the retailer page path /dealer/, as well as the dealer uuid.
 * If both of these are present, we assume it's a retailer page.
 *
 * @param dealerInfoItem The dealer info item.
 * @param dealerUuid The dealer uuid.
 * @returns True if the dealer link is a retailer page, false otherwise.
 */
export const isRetailerPage = (dealerInfoItem: DealerInfoItemType, dealerUuid: string): boolean =>
    dealerInfoItem.type === "website" &&
    dealerInfoItem.link.includes("/dealer/") &&
    dealerInfoItem.link.includes(dealerUuid);
